export const EXPLORE_TERMINAL_STREAM_ID = '01KWY8AA9Y1H62Z34JVJ8P1KZ1';

export const EXPLORE_TERMINAL_POST_LIMIT = 4;

export type TerminalPost = {
  id: string;
  time: string;
  headline: string;
  source: string;
  marketIds: string[];
};

export type TerminalSlide = {
  postId: string;
  marketId: string;
};

export const marketIdsFromEnrichments = (
  polymatcher: readonly string[] | null | undefined,
): string[] => (polymatcher ?? []).filter(Boolean);

const prefixedSource = /^\*\*([^*]+)\*\*:\s*/;

export const splitPrefixedPost = (
  text: string,
): { source: string; headline: string } => {
  const collapsed = text.replace(/\s+/g, ' ').trim();
  const match = prefixedSource.exec(collapsed);
  const source = match?.[1]?.trim();
  if (!match || !source) return { source: '', headline: collapsed };
  return { source, headline: collapsed.slice(match[0].length).trim() };
};

export const formatPostClock = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${date.getHours()}:${minutes}:${seconds}`;
};

export const slidesForPosts = (
  posts: readonly TerminalPost[],
): TerminalSlide[] =>
  posts.flatMap((post) =>
    post.marketIds.map((marketId) => ({ postId: post.id, marketId })),
  );

export const slideIndexFor = (
  slides: readonly TerminalSlide[],
  active: TerminalSlide | null,
): number => {
  if (!active) return 0;
  const exact = slides.findIndex(
    (slide) =>
      slide.postId === active.postId && slide.marketId === active.marketId,
  );
  if (exact >= 0) return exact;
  return Math.max(
    0,
    slides.findIndex((slide) => slide.postId === active.postId),
  );
};

export const stepSlide = (
  index: number,
  delta: number,
  length: number,
): number => {
  if (length <= 0) return 0;
  return (index + delta + length) % length;
};

export const yesNoCents = (
  bestBid: number,
  bestAsk: number,
): { yes: string; no: string } | null => {
  const mark =
    bestBid > 0 && bestAsk > 0 ? (bestBid + bestAsk) / 2 : bestBid || bestAsk;
  if (!(mark > 0) || mark > 1) return null;
  const yes = Math.round(mark * 100);
  return { yes: `${yes}¢`, no: `${Math.min(99, 100 - yes)}¢` };
};

const compactAmount = (value: number): string =>
  String(value >= 10 ? Math.round(value) : Math.round(value * 10) / 10);

export const formatVolume = (volume: number): string => {
  if (!Number.isFinite(volume) || volume <= 0) return '';
  if (volume >= 1_000_000) {
    return `$${compactAmount(volume / 1_000_000)}M volume`;
  }
  if (volume >= 1_000) return `$${compactAmount(volume / 1_000)}K volume`;
  return `$${Math.round(volume)} volume`;
};

export const padIndex = (value: number): string =>
  String(value).padStart(2, '0');

export const marketIdsToFetch = (
  marketKey: string,
  loaded: Readonly<Record<string, unknown>>,
): string[] => {
  if (!marketKey) return [];
  return marketKey.split('|').filter((id) => loaded[id] === undefined);
};

const idsOf = (posts: readonly TerminalPost[]): string =>
  posts.map((post) => post.id).join('\n');

export const terminalRoll = (
  current: readonly TerminalPost[],
  next: readonly TerminalPost[],
): { track: TerminalPost[]; shift: number } | null => {
  if (current.length === 0 || current.length !== next.length) return null;
  const nextIds = new Set(next.map((post) => post.id));
  const leaving = current.filter((post) => !nextIds.has(post.id));
  if (leaving.length === 0) return null;
  const track = [...next, ...leaving];
  const start = track.slice(leaving.length, leaving.length + current.length);
  if (idsOf(start) !== idsOf(current)) return null;
  return { track, shift: leaving.length };
};
